/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { STACCollection, STACItem } from './api';
import {
  fetchStacCollection,
  fetchStacItemsPage,
  fetchStacItemsWithPagination,
  getStacItemsUrl,
} from './api';

const DEFAULT_STALE_TIME = 60000; // One minute

export const useStacCollection = (
  stacBaseUrlWithCollection: string,
  instanceId = '',
  enabled = true,
): UseQueryResult<STACCollection, Error> => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    queryKey: [stacBaseUrlWithCollection, instanceId, 'useStacCollection'],
    queryFn: async (): Promise<STACCollection> =>
      fetchStacCollection(stacBaseUrlWithCollection),
    enabled: stacBaseUrlWithCollection.length > 0 && enabled,
  });
};

export const useStacItems = (
  stacBaseUrlWithCollection: string,
  startDate?: string,
  endDate?: string,
): UseQueryResult<STACItem[], Error> => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    queryKey: ['useStacItems', stacBaseUrlWithCollection, startDate, endDate],
    queryFn: async (): Promise<STACItem[]> => {
      const queryUrl = getStacItemsUrl(
        stacBaseUrlWithCollection,
        startDate,
        endDate,
      );
      const items = await fetchStacItemsWithPagination(queryUrl);
      return items;
    },
    enabled: stacBaseUrlWithCollection.length > 0 && !!startDate && !!endDate,
  });
};

export const useStacTemporalStepItems = (
  stacBaseUrlWithCollection: string,
  startDate?: string,
  endDate?: string,
  enabled = true,
): UseQueryResult<STACItem[], Error> => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    queryKey: [
      'useStacTemporalStepItems',
      stacBaseUrlWithCollection,
      startDate,
      endDate,
    ],
    queryFn: async (): Promise<STACItem[]> => {
      const queryUrl = getStacItemsUrl(
        stacBaseUrlWithCollection,
        startDate,
        endDate,
      );
      return fetchStacItemsPage(queryUrl);
    },
    enabled:
      stacBaseUrlWithCollection.length > 0 &&
      !!startDate &&
      !!endDate &&
      enabled,
  });
};
